/**
 * Catálogo AR del GeoSim = exactamente /admin/wishlist-config (“CTA por región”).
 *
 * Source of truth (misma que WishlistAdmin):
 *   - AR_PROVINCES, AR_CITY_CATALOG → wishlistGeoCatalog.ts
 *   - Agrupación UI → wishlistCityCatalogUi.buildProvinceCityGroups
 *   - Persistencia city_ar.geo_code → wishlistGeoPersistence
 *   - Worker: city_ar (cf.city) > province_ar > country
 *
 * No hay param `neighborhood` en el Worker de esta brand:
 * los barrios CABA son filas city_ar (geo_code: palermo, barracas, …).
 */

import {
  AR_CITY_CATALOG,
  AR_PROVINCES,
  normalizeCityKey,
  wishlistCityLookupKeys,
  type CityCatalogEntry,
  type GeoCtaType,
} from "@/config/wishlistGeoCatalog";
import {
  buildProvinceCityGroups,
  type ProvinceCityGroup,
} from "@/config/wishlistCityCatalogUi";

export {
  AR_CITY_CATALOG,
  AR_PROVINCES,
  normalizeCityKey,
  wishlistCityLookupKeys,
  buildProvinceCityGroups,
};
export type { CityCatalogEntry, GeoCtaType, ProvinceCityGroup };

/** Provincias = filas province_ar del admin (geo_code = code). */
export const GEO_SIM_AR_PROVINCES = AR_PROVINCES;

export function isCabaBarrioEntry(c: CityCatalogEntry): boolean {
  return c.region.startsWith("Barrios CABA");
}

export function isCabaPrincipalEntry(c: CityCatalogEntry): boolean {
  return c.region === "CABA principal" || c.geo_code === "caba";
}

/**
 * Valor de ?city= / cf.city simulado para matchear city_ar del admin.
 * - CABA capital: "Buenos Aires" (lo que reporta CF; alias → geo_code caba)
 * - Resto: `name` del catálogo (normaliza al mismo geo_code que guarda el admin)
 */
export function simCityParamForEntry(c: CityCatalogEntry): string {
  if (isCabaPrincipalEntry(c)) return "Buenos Aires";
  return c.name;
}

/** Label idéntico en espíritu al admin: nombre + geo_code + región. */
export function arCityOptionLabel(c: CityCatalogEntry): string {
  const kind = isCabaBarrioEntry(c)
    ? "barrio"
    : isCabaPrincipalEntry(c)
      ? "capital"
      : c.region.startsWith("GBA")
        ? c.region
        : c.region === "Interior BA" || c.region === "Costa BA"
          ? c.region
          : "ciudad";
  return `${c.name} · ${c.geo_code} · ${kind}`;
}

export function citiesForArProvince(provinceCode: string): CityCatalogEntry[] {
  const code = (provinceCode || "").trim();
  if (!code) return [];
  return AR_CITY_CATALOG.filter((c) => c.provinceCode === code).sort((a, b) => {
    if (code === "CABA") {
      if (isCabaPrincipalEntry(a) && !isCabaPrincipalEntry(b)) return -1;
      if (!isCabaPrincipalEntry(a) && isCabaPrincipalEntry(b)) return 1;
    }
    const reg = a.region.localeCompare(b.region, "es");
    if (reg !== 0) return reg;
    return a.name.localeCompare(b.name, "es");
  });
}

/** Misma agrupación provincia→región→ciudades que pestaña Ciudades del admin. */
export function provinceCityGroupsForSim(
  provinceFilter: string = "all",
): ProvinceCityGroup[] {
  return buildProvinceCityGroups(AR_CITY_CATALOG, "", provinceFilter);
}

export function findArCityEntry(
  provinceCode: string | null | undefined,
  city: string | null | undefined,
): CityCatalogEntry | null {
  const cityKey = normalizeCityKey(city || "");
  if (!cityKey) return null;

  const pool = provinceCode
    ? AR_CITY_CATALOG.filter((c) => c.provinceCode === provinceCode)
    : AR_CITY_CATALOG;

  const matchIn = (list: CityCatalogEntry[]) => {
    for (const c of list) {
      if (normalizeCityKey(c.geo_code) === cityKey) return c;
      if (normalizeCityKey(c.name) === cityKey) return c;
      for (const a of c.aliases ?? []) {
        if (normalizeCityKey(a.geo_code) === cityKey) return c;
        if (normalizeCityKey(a.name) === cityKey) return c;
      }
      // sim param "Buenos Aires" → caba
      if (
        isCabaPrincipalEntry(c) &&
        wishlistCityLookupKeys(city || "").some(
          (k) => normalizeCityKey(c.geo_code) === k || k === "caba",
        )
      ) {
        return c;
      }
    }
    return null;
  };

  const hit = matchIn(pool);
  if (hit) return hit;
  if (provinceCode) return matchIn(AR_CITY_CATALOG);
  return null;
}

export function findArCityByGeoCode(geoCode: string): CityCatalogEntry | null {
  const key = normalizeCityKey(geoCode);
  if (!key) return null;
  return (
    AR_CITY_CATALOG.find((c) => normalizeCityKey(c.geo_code) === key) ?? null
  );
}

/**
 * Atajos = subset del catálogo real (geo_code canónico admin).
 * Nunca inventar nombres fuera de AR_CITY_CATALOG.
 */
export const GEO_SIM_AR_QUICK_CITIES: Array<{
  label: string;
  province: string;
  /** geo_code canónico city_ar (admin) */
  geoCode: string;
  /** cf.city sim param */
  city: string;
  kind: "capital" | "barrio" | "gba" | "interior";
}> = (() => {
  const pick = (
    geoCode: string,
    label: string,
    kind: "capital" | "barrio" | "gba" | "interior",
  ) => {
    const e = findArCityByGeoCode(geoCode);
    if (!e) return null;
    return {
      label,
      province: e.provinceCode,
      geoCode: e.geo_code,
      city: simCityParamForEntry(e),
      kind,
    };
  };
  return [
    pick("caba", "CABA · capital (geo: caba)", "capital"),
    pick("palermo", "CABA · Palermo", "barrio"),
    pick("recoleta", "CABA · Recoleta", "barrio"),
    pick("barracas", "CABA · Barracas", "barrio"),
    pick("puerto madero", "CABA · Puerto Madero", "barrio"),
    pick("monserrat", "CABA · Monserrat", "barrio"),
    pick("belgrano", "CABA · Belgrano", "barrio"),
    pick("tigre", "GBA · Tigre", "gba"),
    pick("san isidro", "GBA · San Isidro", "gba"),
    pick("la matanza", "GBA · La Matanza", "gba"),
    pick("quilmes", "GBA · Quilmes", "gba"),
    pick("la plata", "BA · La Plata", "interior"),
    pick("mar del plata", "BA · Mar del Plata", "interior"),
    pick("cordoba", "Córdoba · capital", "interior"),
    pick("rosario", "Santa Fe · Rosario", "interior"),
    pick("mendoza", "Mendoza · capital", "interior"),
  ].filter((x): x is NonNullable<typeof x> => x != null);
})();

/** @deprecated alias — use simCityParamForEntry */
export function cityParamForCatalogEntry(c: CityCatalogEntry): string {
  return simCityParamForEntry(c);
}
